import React, { useEffect, useState } from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { useUIStore } from '../stores/useUIStore';
import { Clock, User, FileCode, Copy, Maximize2, Hash, CheckCircle2, ShieldCheck, Share2, Code2, Eye } from 'lucide-react';
import mermaid from 'mermaid';

export const ReaderView: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { activeDocumentPath, setActiveHeading } = useDocumentStore();
  const { openLightbox } = useUIStore();
  const [showRawMarkdown, setShowRawMarkdown] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const doc = currentPackage?.documents.find((d) => d.path === activeDocumentPath) || currentPackage?.documents[0];

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      securityLevel: 'loose',
    });
  }, []);

  if (!doc) {
    return (
      <div className="flex-1 p-8 text-center text-slate-400 text-sm flex flex-col items-center justify-center font-mono">
        <FileCode className="w-12 h-12 text-slate-600 mb-3" />
        <p>Select a document from the sidebar to start reading.</p>
      </div>
    );
  }

  const handleCopyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const completedTasks = doc.tasks.filter(t => t.status === 'completed').length;

  return (
    <div className="flex-1 flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Main Document Reader Content Pane */}
      <main className="flex-1 overflow-y-auto p-8 max-w-4xl mx-auto space-y-6">
        {/* Document Header Metadata Banner */}
        <div className="glass-panel p-6 rounded-2xl border border-white/10 space-y-4 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 font-mono">
              <span className="text-xs px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 font-bold uppercase tracking-wider">
                {doc.meta.docId || doc.name}
              </span>
              {doc.meta.version && (
                <span className="text-xs px-2 py-0.5 rounded-lg bg-slate-800 text-slate-400 border border-slate-700">
                  v{doc.meta.version}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {doc.meta.status && (
                <span className="text-xs font-mono px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 uppercase font-bold flex items-center space-x-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>{doc.meta.status}</span>
                </span>
              )}

              <button
                onClick={() => setShowRawMarkdown(!showRawMarkdown)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono flex items-center space-x-1 transition-colors border border-slate-700"
              >
                {showRawMarkdown ? <Eye className="w-3.5 h-3.5 text-blue-400" /> : <Code2 className="w-3.5 h-3.5 text-cyan-400" />}
                <span>{showRawMarkdown ? 'Rendered' : 'Raw Spec'}</span>
              </button>
            </div>
          </div>

          <h1 className="text-2xl font-extrabold text-white tracking-tight leading-tight">
            {doc.meta.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-xs text-slate-400 font-mono pt-3 border-t border-white/5">
            {doc.meta.author && (
              <span className="flex items-center space-x-1.5 text-slate-300">
                <User className="w-3.5 h-3.5 text-blue-400" />
                <span>{doc.meta.author}</span>
              </span>
            )}
            <span className="flex items-center space-x-1.5">
              <Clock className="w-3.5 h-3.5 text-cyan-400" />
              <span>{doc.meta.readingTimeMinutes} min read</span>
            </span>
            <span className="flex items-center space-x-1.5 text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{completedTasks}/{doc.tasks.length} Action Tasks</span>
            </span>
          </div>
        </div>

        {/* Raw Markdown Code View Mode */}
        {showRawMarkdown ? (
          <div className="glass-panel p-6 rounded-2xl border border-slate-800 bg-obsidian-950 font-mono text-xs text-cyan-300 overflow-x-auto leading-relaxed whitespace-pre-wrap">
            {doc.content}
          </div>
        ) : (
          /* Formatted Markdown Article */
          <article className="prose prose-invert max-w-none prose-headings:scroll-mt-20 prose-headings:font-bold prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:text-slate-300 prose-p:text-sm prose-p:leading-relaxed prose-code:text-cyan-300 prose-pre:bg-obsidian-950 prose-pre:border prose-pre:border-slate-800 space-y-5">
            {doc.content.split('\n\n').map((block, idx) => {
              if (block.startsWith('# ')) {
                const text = block.replace('# ', '');
                const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                return (
                  <h1 key={idx} id={id} className="text-xl font-extrabold text-white pt-6 pb-2 border-b border-slate-800/80 flex items-center group">
                    <span>{text}</span>
                    <a href={`#${id}`} className="ml-2 text-slate-600 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Hash className="w-4 h-4" />
                    </a>
                  </h1>
                );
              }

              if (block.startsWith('## ')) {
                const text = block.replace('## ', '');
                const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                return (
                  <h2 key={idx} id={id} className="text-lg font-bold text-slate-100 pt-4 pb-1 border-b border-slate-800/50 flex items-center group">
                    <span>{text}</span>
                    <a href={`#${id}`} className="ml-2 text-slate-600 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Hash className="w-3.5 h-3.5" />
                    </a>
                  </h2>
                );
              }

              if (block.startsWith('```mermaid')) {
                const mermaidCode = block.replace('```mermaid\n', '').replace('\n```', '');
                return (
                  <div key={idx} className="my-6 glass-panel p-5 rounded-2xl border border-blue-500/20 relative group shadow-lg">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/5 text-xs text-slate-400 font-mono">
                      <span className="text-blue-400 font-bold flex items-center space-x-1.5">
                        <Share2 className="w-3.5 h-3.5 text-cyan-400" />
                        <span>Architecture Diagram Schematic</span>
                      </span>
                      <button
                        onClick={() => openLightbox(`<svg width="100%" height="100%"><text x="20" y="40" fill="#3b82f6" font-size="16">${mermaidCode}</text></svg>`, 'Mermaid Architecture Diagram')}
                        className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center space-x-1.5 transition-colors text-[11px] font-mono border border-slate-700 shadow-sm"
                      >
                        <Maximize2 className="w-3 h-3 text-cyan-400" />
                        <span>Expand Lightbox</span>
                      </button>
                    </div>
                    <pre className="text-xs font-mono text-cyan-200 bg-obsidian-950 p-4 rounded-xl overflow-x-auto border border-slate-800">
                      {mermaidCode}
                    </pre>
                  </div>
                );
              }

              if (block.startsWith('```')) {
                const codeLines = block.split('\n');
                const lang = codeLines[0].replace('```', '');
                const codeText = codeLines.slice(1, -1).join('\n');
                const blockId = `code-block-${idx}`;
                return (
                  <div key={idx} className="my-4 rounded-2xl bg-obsidian-950 border border-slate-800 overflow-hidden shadow-lg group">
                    <div className="px-4 py-2.5 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between text-xs text-slate-400 font-mono">
                      <span className="text-cyan-400 font-semibold uppercase text-[10px] tracking-wider">{lang || 'code'}</span>
                      <button
                        onClick={() => handleCopyCode(codeText, blockId)}
                        className="flex items-center space-x-1.5 hover:text-slate-100 transition-colors text-[11px] px-2 py-0.5 rounded bg-slate-800 border border-slate-700"
                      >
                        <Copy className="w-3 h-3" />
                        <span>{copiedCode === blockId ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <pre className="p-4 text-xs font-mono text-slate-200 overflow-x-auto leading-relaxed">
                      {codeText}
                    </pre>
                  </div>
                );
              }

              return (
                <p key={idx} className="text-sm text-slate-300 leading-relaxed">
                  {block}
                </p>
              );
            })}
          </article>
        )}
      </main>

      {/* Table of Contents Right Sticky Sidebar */}
      <aside className="w-64 glass-panel border-l border-white/10 p-5 hidden lg:block overflow-y-auto h-[calc(100vh-4rem)]">
        <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center space-x-1.5">
          <Hash className="w-3.5 h-3.5 text-blue-400" />
          <span>On This Page</span>
        </h3>
        <nav className="space-y-1 text-xs">
          {doc.headings.map((h) => (
            <a
              key={h.id}
              href={`#${h.id}`}
              onClick={() => setActiveHeading(h.id)}
              className={`block truncate py-1.5 transition-colors font-mono ${
                h.depth === 1
                  ? 'font-semibold text-slate-200 pl-0 border-b border-white/5 pb-1'
                  : 'text-slate-400 hover:text-slate-200 pl-3 border-l border-slate-800'
              }`}
            >
              {h.text}
            </a>
          ))}
        </nav>
      </aside>
    </div>
  );
};
